import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Icon } from '../../components/Icon';
import { useApiMutation, useApiQuery } from '../../utils/apiHooks';
import { ApiRequest } from '../../utils/api';
import { OfferingType } from '../../utils/offeringType';
import { Product } from '../../model/Products';
import { ConfirmModal } from '../../components/ConfirmModal';
import { Contact } from '../../model/Account';
import { gaB2CChangeSubscriptionType } from '../../utils/analytics';

interface Props {
	productPackageType: any;
	offeringType: OfferingType;
	customerContactData: Contact;
	subscriptionNumber: string;
	pollCurrentSim: () => void;
}

const SHOW_RAJATON_TURBO_CAMPAIGN = false;

const SelectionContainer = styled.div``;

const ProductRow = styled.div<{ isSelectable: boolean }>`
	padding: 1em 0 1em 0;
	.package-name {
		font-size: 1.25rem;
	}
	button {
		padding: 0.6em 0.5em 0.6em 0.5em;
		margin-top: 0.5em;
		border-radius: 10px;
		min-width: 10em;
		background-color: white;
		color: red;
		border: 2px solid red;
		float: right;
		${(props) => (props.isSelectable ? 'cursor: pointer;' : '')}
	}

	.grayed-out {
		background-color: white;
		border: 2px solid #969696;
		color: #969696;
	}

	.selected {
		background-color: #eaeaea;
		border: 2px solid #eaeaea;
		color: black;
		float: right;
	}
	border-bottom: 1px solid grey;

	.info {
		line-height: 1.2em;
	}

	.info-text {
		display: block;
		max-width: 65%;
	}

	button:focus {
		outline: none;
	}
`;

const PackageUpdateNotice = styled.div`
	overflow: hidden;
	position: relative;
	.red-text {
		color: red;
	}

	.alert {
		min-height: 10em;
		display: inline-block;
		vertical-align: top;
		margin: 2.2em 0.6em 0 0;
	}
	.text {
		margin: 2em 0 0 0;
		display: inline-block;
		width: 80%;
	}
`;

const CampaignText = styled.div`
	h4 {
		color: #f00;
		font-size: 1.35rem;
	}
	p {
		font-weight: bold;
	}
`;

const MoiPrices = styled.div`
	border-bottom: 1px solid grey;
	padding-top: 0.5rem;
	padding-bottom: 1rem;

	h4 {
		font-size: 1.35rem;
	}
	.leaders {
		text-align: left;
		max-width: 25.5rem;
		padding: 0;
		overflow-x: hidden;
		list-style: none;
		color: #444;
		line-height: 1.5rem;

		@media screen and (max-width: 799px) {
			margin: 0;
			border-left: 1px solid #f4f4f4;
			padding-left: 2.5rem;

			.dotted-row {
				padding-bottom: 0.75rem;
			}

			.dotted-row:last-of-type {
				padding-bottom: 0;
			}
		}

		@media screen and (max-width: 640px) {
			padding-left: 1rem;

			.dotted-row {
				padding-bottom: 0.5rem;
			}
		}

		@media screen and (max-width: 1000px) {
			font-size: 1.1rem;
			line-height: 1.3rem;
		}

		.dotted-row:before {
			float: left;
			width: 0;
			white-space: nowrap;
			content: '. . . . . . . . . . . . . . . . . . . . ' '. . . . . . . . . . . . . . . . . . . . ' '. . . . . . . . . . . . . . . . . . . . ' '. . . . . . . . . . . . . . . . . . . . ';
		}

		.leaders-title {
			padding-right: 0.33em;
			background: white;
		}

		.leaders-value {
			float: right;
			padding-left: 0.33em;
			background: white;
		}

		@media screen and (max-width: 799px) {
			font-size: 80%;
			display: block;
		}
	}
`;

const isSelectableProduct = (currentPackage: Product, selectedPackageType: string, product: Product) => {
	if (product.orderItemCode !== currentPackage.orderItemCode && product.orderItemCode !== selectedPackageType) {
		return true;
	} else if (selectedPackageType !== undefined && product.orderItemCode !== selectedPackageType) {
		return true;
	}
	return false;
};

const renderPackage = (product: Product, currentPackage: Product, selectedPackageType: string, onClick: any, key: number, offeringType: OfferingType) => {
	const { displayName, orderItemCode, dataSpeedMbs, eurPerMoi, maxEurMoisPerMonth, addToSharedDataGbAmount, euDataLimitMB } = product;
	const buttonClass = () => {
		if (currentPackage.orderItemCode === orderItemCode) {
			return 'grayed-out';
		} else if (orderItemCode === selectedPackageType) {
			return 'selected';
		} else {
			return 'new';
		}
	};

	const hasUnlimitedVoice = product.orderItemCode.endsWith('ULV');
	const hasUnlimitedData = product.dataPackage === 'unlimited';

	return (
		<ProductRow isSelectable={isSelectableProduct(currentPackage, selectedPackageType, product)} key={key}>
			<div className={'title-row'}>
				<span className={'package-name'}>{displayName}</span>
				<button className={buttonClass()} onClick={() => onClick(product)} disabled={!isSelectableProduct(currentPackage, selectedPackageType, product)}>
					{buttonClass() === 'grayed-out' ? 'Nykyinen liittymäsi' : buttonClass() === 'selected' ? 'Valitsemasi uusi liittymä' : 'Valitse'}
				</button>
			</div>
			<div className={'info'}>
				<span className={'info-text'}>{`${dataSpeedMbs} Mbit/s, ${!hasUnlimitedData ? `${addToSharedDataGbAmount}Gt dataa` : 'Rajaton määrä dataa'},`}</span>
				{offeringType === OfferingType.CONSUMER4 && <span>{`EU-roaming ${parseInt(euDataLimitMB) / 1000} Gt`}</span>}
				{offeringType !== OfferingType.CONSUMER4 && (
					<React.Fragment>
						<span className={'info-text'}>{!hasUnlimitedVoice ? `Puhelut ${eurPerMoi}€/min` : 'Rajattomasti kotimaan puhetta'}</span>
						<span className={'info-text'}>{` ja tekstiviestit ${eurPerMoi}€/kpl`}</span>
					</React.Fragment>
				)}
				{offeringType === OfferingType.CONSUMER3 && <span>{`...mutta aina enintään ${maxEurMoisPerMonth}€/kk`}</span>}
			</div>
			{orderItemCode === 'B2C_PACK_FAST' && SHOW_RAJATON_TURBO_CAMPAIGN && (
				<CampaignText>
					<h4>TURBO RAJATTOMAN HINNALLA KOLMEKSI KUUKAUDEKSI!</h4>
					<p>Saat Moin vanhana asiakkaana RAJATON TURBO -liittymän 3 kuukaudeksi alennettuun hintaan 18 €/kk (norm. 23 €/kk)</p>
				</CampaignText>
			)}
		</ProductRow>
	);
};

export const ProductPackageTypeSelection: React.FC<Props> = (props: Props) => {
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [selectedNewPackage, setSelectedNewPackage] = useState<Product | undefined>(undefined);
	const [selectedProductPackageType, setSelectedProductPackageType] = useState<string>('');
	const [productPackageChangeAppliedDate, setProductPackageChangeAppliedDate] = useState<string | undefined>('');
	const [currentPackage, setCurrentPackage] = useState<Product | undefined>(undefined);

	const { data: products } = useApiQuery(ApiRequest.getProducts(props.offeringType), { cacheTime: 60000 });
	const [updateSim, { data: updateSimData }] = useApiMutation((data: any) => ApiRequest.updateSim(props.subscriptionNumber, data));

	useEffect(() => {
		setPackageValues(props.productPackageType);
	}, [props.productPackageType, products]); // eslint-disable-line

	useEffect(() => {
		if (updateSimData !== undefined) {
			setPackageValues(updateSimData.settings?.productPackageType);
			if (updateSimData.settings?.productPackageType?.updating && updateSimData.settings?.productPackageType?.changeAppliedDate === undefined) {
				_.delay(() => {
					props.pollCurrentSim();
				}, 5000);
			}
		}
	}, [updateSimData]); // eslint-disable-line

	const setPackageValues = (productPackageType: any) => {
		if (products !== undefined) {
			setCurrentPackage(_.find(products.productPackagePrices, (val: any) => val.orderItemCode === productPackageType?.value));
		}
		setSelectedProductPackageType(productPackageType?.latestChangeRequestValue);
		setProductPackageChangeAppliedDate(productPackageType?.changeAppliedDate);
	};

	const onConfirm = () => {
		setModalIsOpen(false);
		updateSim({ id: 'productPackageType', value: selectedNewPackage?.orderItemCode });
		gaB2CChangeSubscriptionType(currentPackage!.orderItemCode, selectedNewPackage!.orderItemCode, selectedNewPackage!.eurPerMonth - currentPackage!.eurPerMonth);
	};

	const closeModal = () => {
		setModalIsOpen(false);
	};

	const onChange = (product: Product) => {
		setModalIsOpen(true);
		setSelectedNewPackage(product);
	};

	if (currentPackage === undefined || products === undefined) {
		return null;
	} else {
		return (
			<React.Fragment>
				<ConfirmModal
					isOpen={modalIsOpen}
					onRequestClose={closeModal}
					onConfirm={onConfirm}
					title={'Vahvista liittymän vaihto'}
					message={`Olet vaihtamassa liittymäsi ${selectedNewPackage?.displayName} -liittymään, jonka hinta on ${selectedNewPackage?.eurPerMonth} €/kk.`}
					productPackageTypeValues={products.productPackagePrices}
					selectedSubscriptionType={selectedNewPackage?.orderItemCode}
					offeringType={props.offeringType}
				/>
				<SelectionContainer>
					{products.productPackagePrices.map((product: Product, key: number) => renderPackage(product, currentPackage!, selectedProductPackageType, onChange, key, props.offeringType))}
					{props.offeringType === OfferingType.CONSUMER4 && (
						<MoiPrices>
							<h4>Moi-liittymien puhelu ja SMS-hinnoittelu</h4>
							<ul className={'leaders'}>
								<li className={'dotted-row'}>
									<span className={'leaders-title'}>Puhelut </span>
									<span className={'leaders-value'}>{currentPackage.eurPerMoi} €/min</span>
								</li>
								<li className={'dotted-row'}>
									<span className={'leaders-title'}>Tekstiviestit </span>
									<span className={'leaders-value'}>{currentPackage.eurPerMoi} €/kpl</span>
								</li>
							</ul>
							<div>Kaikissa liittymissä puhelut ja kotimaan tekstarit aina enintään {currentPackage.maxEurMoisPerMonth}€/kk!</div>
						</MoiPrices>
					)}
					{selectedProductPackageType !== '' && productPackageChangeAppliedDate !== undefined && (
						<PackageUpdateNotice>
							<div className={'alert'}>
								<Icon name="small--alert" />
							</div>
							<span className={'text'}>
								Valitsemasi uusi liittymä astuu voimaan {productPackageChangeAppliedDate} jälkeen, kun laskutuskausi vaihtuu. Siihen saakka käytössäsi on &apos;{currentPackage?.displayName}
								&apos; liittymä, joka on nykyinen liittymäsi. Jos haluat mieluummin pitää nykyisen liittymäsi, tai valita kokonaan uuden liittymätyypin, suorita valinta uudelleen
							</span>
						</PackageUpdateNotice>
					)}
				</SelectionContainer>
			</React.Fragment>
		);
	}
};
